import { Condition, type Operator, type Stat, type Window } from "@/lib/api";
import type { EntityOption } from "@/lib/entities";
import { nextID } from "@/lib/utils";

export interface AmbiguousSubject {
	options: EntityOption[];
	phrase: string;
}

export interface ParsedRule {
	ambiguous?: AmbiguousSubject;
	condition: Condition;
	missing: string[];
}

const STAT_ALIASES: Array<{ stat: Stat; patterns: RegExp[] }> = [
	{ stat: "hr", patterns: [/\bhome runs?\b/i, /\bhomer(?:s)?\b/i, /\bhr\b/i] },
	{ stat: "rbi", patterns: [/\brbi\b/i, /\bruns batted in\b/i] },
	{
		stat: "obp",
		patterns: [/\bon[- ]base percentage\b/i, /\bobp\b/i],
	},
	{
		stat: "slg",
		patterns: [/\bslugging(?: percentage)?\b/i, /\bslg\b/i],
	},
	{
		stat: "avg",
		patterns: [/\bbatting average\b/i, /\baverage\b/i, /\bavg\b/i],
	},
];

const findStat = (text: string): Stat | undefined =>
	STAT_ALIASES.find(({ patterns }) =>
		patterns.some((pattern) => pattern.test(text)),
	)?.stat;

const findOperator = (text: string): Operator => {
	if (/\bat least\b/i.test(text)) return "gte";
	if (/\b(?:more than|over|greater than)\b/i.test(text)) return "gt";
	if (/\bat most\b/i.test(text)) return "lte";
	if (/\b(?:fewer than|less than|under|below)\b/i.test(text)) return "lt";
	if (/\bexactly\b/i.test(text)) return "eq";

	return "gte";
};

const findWindow = (text: string): Window =>
	/\binning\b/i.test(text) ? "inning" : "game";

const findOver = (text: string): number => {
	const match = text.match(/\b(\d+)\s+(?:consecutive\s+)?innings?\b/i);
	const value = match ? Number(match[1]) : 1;

	return Number.isFinite(value) && value > 0 ? value : 1;
};

const findValue = (
	text: string,
	stat: Stat | undefined,
): number | undefined => {
	const match = text.match(/(\.\d+|\d+(?:\.\d+)?)/);

	if (match) {
		const value = Number(match[1]);
		return Number.isFinite(value) ? value : undefined;
	}

	if (stat === "hr" && /\b(?:a|an|one)\s+(?:home run|homer)\b/i.test(text)) {
		return 1;
	}

	return undefined;
};

const aliasScore = (option: EntityOption, text: string): number => {
	const haystack = text.toLowerCase();
	let best = 0;

	const aliases = [option.label.toLowerCase(), ...option.search.split(/\s+/)];

	for (const alias of aliases) {
		if (alias.length >= 3 && haystack.includes(alias)) {
			best = Math.max(best, alias.length);
		}
	}

	return best;
};

const findSubject = (
	text: string,
	options: EntityOption[],
): { ambiguous?: AmbiguousSubject; option?: EntityOption } => {
	const scored = options
		.map((option) => ({ option, score: aliasScore(option, text) }))
		.filter(({ score }) => score > 0)
		.sort((a, b) => b.score - a.score);

	if (scored.length === 0) {
		return {};
	}

	const best = scored[0];
	const tied = scored.filter(({ score }) => score === best.score);

	if (tied.length > 1) {
		const phrase = text.match(/\b[A-Z][a-z'.-]+(?:\s+[A-Z][a-z'.-]+)*/)?.[0];

		return {
			ambiguous: {
				options: tied.map(({ option }) => option),
				phrase: phrase ?? "That name",
			},
		};
	}

	return { option: best.option };
};

const stripAppTail = (text: string): string =>
	text.replace(/(?:,\s*)?\b(?:open|launch)\b[\s\S]*$/i, "").trim();

const splitClauses = (text: string): string[] =>
	stripAppTail(text)
		.split(/\s+and\s+(?![^,]*\bopen\b)/i)
		.map((clause) => clause.trim())
		.filter(Boolean);

const applyClause = (
	condition: Condition,
	clause: string,
	options: EntityOption[],
	previous?: EntityOption,
): {
	ambiguous?: AmbiguousSubject;
	missing: string[];
	option?: EntityOption;
} => {
	const subject = findSubject(clause, options);
	const option = subject.option ?? previous;
	const stat = findStat(clause);
	const value = findValue(clause, stat);

	if (option) {
		condition.entity = option.value;
	}

	if (stat) {
		condition.stat = stat;
	}

	condition.operator = findOperator(clause);
	condition.window = findWindow(clause);
	condition.over = findOver(clause);

	if (value !== undefined) {
		condition.value = value;
	}

	const missing: string[] = [];

	if (!option) {
		missing.push("who to watch");
	}

	if (!stat) {
		missing.push("which stat");
	}

	if (value === undefined) {
		missing.push("the number to hit");
	}

	return {
		ambiguous: subject.ambiguous,
		missing,
		option,
	};
};

export const parseRule = (
	text: string,
	options: EntityOption[],
): ParsedRule => {
	const clauses = splitClauses(text);
	const conditions: Condition[] = [];
	const missing = new Set<string>();
	let ambiguous: AmbiguousSubject | undefined;
	let previous: EntityOption | undefined;

	if (clauses.length === 0) {
		return {
			condition: Condition.makeDefault(),
			missing: ["who to watch", "which stat", "the number to hit"],
		};
	}

	for (const clause of clauses) {
		const condition = Condition.makeDefault();
		condition.value = undefined;
		const result = applyClause(condition, clause, options, previous);

		for (const item of result.missing) {
			missing.add(item);
		}

		if (!ambiguous && result.ambiguous) {
			ambiguous = result.ambiguous;
		}

		if (result.option) {
			previous = result.option;
		}

		conditions.push(condition);
	}

	const condition =
		conditions.length === 1
			? conditions[0]
			: Condition.fromAny({
					id: nextID(),
					ruleType: "and",
					rules: conditions.map((item) => item.toAny()),
					value: undefined,
				});

	return {
		ambiguous,
		condition,
		missing: [...missing],
	};
};
